import React, { Component } from "react";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { withStore } from "../Redux/Index";
import { settingsStore } from "../Redux/Reducers";
import { SystemTokenCard } from "../Components/SystemTokenCard";
import { AdvancedDeploymentTools } from "./Publication/AdvancedPanel";
import BodyMigrationCard from "../Components/BodyMigrationCard";
import DeepAnalyzePanel from "../Components/Checks/DeepAnalyzePanel";
import MediaOrphansCard from "../Components/Checks/MediaOrphansCard";
import GatedButton from "../Components/GatedButton";
import { BaseFilterController } from "../Components/BaseFilter";
import Title from "../Components/Title";
import { client } from "../Clients";
import axios from "axios";
import type {
  BCCADMINBaseModelsDeployedFileRepairResult,
  BCCADMINBaseModelsDeployedFileScanResult,
  BCCADMINBaseModelsDeploymentQueueSeedResult,
  BCCADMINBaseModelsDynamicPathScanResult,
  BCCADMINBaseModelsBrokenLinkScanResult,
  BCCADMINBaseModelsBrokenLinkItem,
  BCCADMINBaseModelsStorageRecompressAllResult
} from "../API/Models";

// Base API depuis l'environnement (JAMAIS le BASE_PATH généré : son défaut est
// http://localhost:5000, ce qui fait appeler localhost depuis l'admin déployé et
// déclenche la permission Chrome « Local Network Access »).
const BASE_PATH = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

/** One scanner issue (mirrors BCC.ADMIN.Base.Models.ContentIntegrityIssue). */
interface IntegrityIssue {
  checkId: string;
  checkTitle: string;
  severity: number; // 0 = Warning, 1 = Error
  detail: string;
  targetKind: string;
  targetId: string;
  affectedCount: number;
  fixable: boolean;
  fixDescription: string;
}
interface IntegrityCheckSummary { checkId: string; title: string; description: string; issueCount: number; }
interface IntegrityReport {
  checks: IntegrityCheckSummary[];
  issues: IntegrityIssue[];
  totalIssues: number;
  errorCount: number;
  warningCount: number;
  isStable: boolean;
  elapsedMs: number;
}
interface IntegrityFixResult {
  fixedIssues: number;
  failedIssues: number;
  actions: string[];
  errors: string[];
  reportAfter: IntegrityReport;
}

type SettingsTab = "integrity" | "content" | "deployment" | "search" | "translations" | "media";

/** Uniform axios-error → user message: server-provided "error"/"message" fields win over the generic axios text. */
const extractApiError = (err: any, fallback: string): string =>
  String(err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? fallback);

export interface State {
  show: boolean;
  loading: boolean;
  activeTab: SettingsTab;
  filterController: BaseFilterController;
  refreshLoading: boolean;
  refreshMessage: string | null;
  scanDynamicLoading: boolean;
  scanDynamicResult: BCCADMINBaseModelsDynamicPathScanResult | null;
  scanDynamicMessage: string | null;
  seedPreviewLoading: boolean;
  seedPreviewResult: BCCADMINBaseModelsDeploymentQueueSeedResult | null;
  seedPreviewMessage: string | null;
  forcePreviewLoading: boolean;
  forcePreviewResult: BCCADMINBaseModelsDeploymentQueueSeedResult | null;
  forcePreviewMessage: string | null;
  linkScanLoading: boolean;
  linkScanResult: BCCADMINBaseModelsBrokenLinkScanResult | null;
  linkScanMessage: string | null;
  recompressAllLoading: boolean;
  recompressAllResult: BCCADMINBaseModelsStorageRecompressAllResult | null;
  recompressAllMessage: string | null;
  rebuildSearchLoading: boolean;
  rebuildSearchMessage: string | null;
  esRebuildLoading: boolean;
  esRebuildMessage: string | null;
  esHealth: any | null;
  esHealthMessage: string | null;
  smartReconcileLoading: boolean;
  smartReconcileMessage: string | null;
  hardCleanTarget: "preview" | "production";
  hardCleanLoading: boolean;
  hardCleanMessage: string | null;
  retranslateLoading: boolean;
  retranslateMessage: string | null;
  previewScanLoading: boolean;
  previewFixLoading: boolean;
  previewScanResult: BCCADMINBaseModelsDeployedFileScanResult | BCCADMINBaseModelsDeployedFileRepairResult | null;
  productionScanLoading: boolean;
  productionFixLoading: boolean;
  productionScanResult: BCCADMINBaseModelsDeployedFileScanResult | BCCADMINBaseModelsDeployedFileRepairResult | null;
  integrityScanLoading: boolean;
  integrityFixLoading: boolean;
  integrityReport: IntegrityReport | null;
  integrityMessage: string | null;
}

type Props = typeof settingsStore.actionCreators &
  settingsStore.ISettingsState &
  RouteComponentProps<{}>;

const TABS: { id: SettingsTab; label: string }[] = [
  { id: "integrity", label: "Intégrité du site" },
  { id: "content", label: "Contenu" },
  { id: "deployment", label: "Déploiement" },
  { id: "search", label: "Recherche" },
  { id: "translations", label: "Traductions" },
  { id: "media", label: "Médias" }
];

/**
 * Settings page: maintenance tooling grouped into tabs, with a first-class "Site integrity" tab
 * that scans the whole site for structural breaks (content linked by id instead of key, records
 * under an unreachable bucket) and fixes them in one click : then confirms the site is stable.
 *
 * Nothing is loaded up-front; every card is fire-and-observe against admin endpoints:
 * /api/integrity/scan|fix, the /api/admin/* scans (dynamic paths, deployed files, broken links,
 * queue seeding), the /api/admin/maintenance/* background jobs (smart-reconcile, hard-clean,
 * retranslate-all, migrate-bodies : the latter owned by BodyMigrationCard),
 * /api/search/index/rebuild and /api/storage/recompress-all. Long-running jobs
 * return 202 and are then watched by client-side polling. The backend coordinator runs ONE
 * maintenance job at a time and publishes its progress through a single shared status route (see
 * the pollers below), so a 409 on start means "already running" : we attach to the in-flight
 * job's status instead of reporting an error.
 *
 * Still a class component (predates the hook-based pages); state is a flat bag of per-action
 * loading/result/message triples, nothing derived.
 */
class Settings extends Component<Props, State> {
  static displayName = Settings.name;

  constructor(props: any) {
    super(props);
    this.state = {
      show: false,
      loading: false,
      activeTab: "integrity",
      filterController: new BaseFilterController(this.onRefresh),
      refreshLoading: false,
      refreshMessage: null,
      scanDynamicLoading: false,
      scanDynamicResult: null,
      scanDynamicMessage: null,
      seedPreviewLoading: false,
      seedPreviewResult: null,
      seedPreviewMessage: null,
      forcePreviewLoading: false,
      forcePreviewResult: null,
      forcePreviewMessage: null,
      linkScanLoading: false,
      linkScanResult: null,
      linkScanMessage: null,
      recompressAllLoading: false,
      recompressAllResult: null,
      recompressAllMessage: null,
      rebuildSearchLoading: false,
      rebuildSearchMessage: null,
      esRebuildLoading: false,
      esRebuildMessage: null,
      esHealth: null,
      esHealthMessage: null,
      smartReconcileLoading: false,
      smartReconcileMessage: null,
      hardCleanTarget: "preview",
      hardCleanLoading: false,
      hardCleanMessage: null,
      retranslateLoading: false,
      retranslateMessage: null,
      previewScanLoading: false,
      previewFixLoading: false,
      previewScanResult: null,
      productionScanLoading: false,
      productionFixLoading: false,
      productionScanResult: null,
      integrityScanLoading: false,
      integrityFixLoading: false,
      integrityReport: null,
      integrityMessage: null
    };
  }

  componentDidMount() {
    this.onRefresh();
    this.loadElasticHealth();
  }

  onRefresh = async () => { };

  // ---- Site integrity --------------------------------------------------------------------------

  handleIntegrityScan = async () => {
    if (this.state.integrityScanLoading) return;
    this.setState({ integrityScanLoading: true, integrityMessage: "Analyse du site en cours…" });
    try {
      const res = await axios.post(`${BASE_PATH}/api/integrity/scan`);
      const report = res?.data as IntegrityReport;
      this.setState({
        integrityReport: report,
        integrityMessage: report.isStable
          ? `✓ Site stable : aucune erreur (${report.warningCount} avertissement(s)). Analyse en ${report.elapsedMs} ms.`
          : `${report.errorCount} erreur(s) et ${report.warningCount} avertissement(s) détecté(s).`
      });
    } catch (err: any) {
      const apiMessage = err?.response?.data?.error ?? err?.message ?? "Échec de l'analyse.";
      this.setState({ integrityMessage: String(apiMessage) });
    } finally {
      this.setState({ integrityScanLoading: false });
    }
  };

  handleIntegrityFix = async (targetIds?: string[]) => {
    if (this.state.integrityFixLoading) return;
    const scope = targetIds && targetIds.length ? "le problème sélectionné" : "tous les problèmes détectés";
    if (!window.confirm(`Corriger automatiquement ${scope} ? Les corrections re-clés les données, re-cuisent les widgets et redéploient les pages concernées (preview + production).`)) {
      return;
    }
    this.setState({ integrityFixLoading: true, integrityMessage: "Correction en cours…" });
    try {
      const res = await axios.post(`${BASE_PATH}/api/integrity/fix`, { targetIds: targetIds ?? null });
      const result = res?.data as IntegrityFixResult;
      this.setState({
        integrityReport: result.reportAfter,
        integrityMessage:
          `${result.fixedIssues} corrigé(s)${result.failedIssues ? `, ${result.failedIssues} en échec` : ""}. ` +
          (result.reportAfter.isStable ? "✓ Le site est maintenant stable." : `${result.reportAfter.errorCount} erreur(s) restante(s).`)
      });
    } catch (err: any) {
      const apiMessage = err?.response?.data?.error ?? err?.message ?? "Échec de la correction.";
      this.setState({ integrityMessage: String(apiMessage) });
    } finally {
      this.setState({ integrityFixLoading: false });
    }
  };

  // ---- existing maintenance handlers (unchanged behaviour) -------------------------------------

  handleRefreshDynamicPaths = async () => {
    if (this.state.refreshLoading) return;
    this.setState({ refreshLoading: true, refreshMessage: null });
    try {
      const response = await client.admin.apiAdminRefreshDynamicPathsPost();
      const data = response.data;
      const message = data
        ? `Updated ${data.updatedItems ?? 0} items. Skipped ${data.skippedMissingData ?? 0} missing, ${data.skippedDuplicate ?? 0} duplicates.`
        : "Dynamic paths refreshed.";
      this.setState({ refreshMessage: message });
    } catch (err: any) {
      this.setState({ refreshMessage: extractApiError(err, "Refresh failed.") });
    } finally {
      this.setState({ refreshLoading: false });
    }
  };

  handleScanDynamicPaths = async () => {
    if (this.state.scanDynamicLoading) return;
    this.setState({ scanDynamicLoading: true, scanDynamicMessage: null });
    try {
      const response = await client.admin.apiAdminScanDynamicPathsPost();
      const data = response.data ?? null;
      const message = data ? `${data.itemsToFix ?? 0} item(s) need path updates.` : "Dynamic path scan completed.";
      this.setState({ scanDynamicResult: data, scanDynamicMessage: message });
    } catch (err: any) {
      this.setState({ scanDynamicMessage: extractApiError(err, "Scan failed.") });
    } finally {
      this.setState({ scanDynamicLoading: false });
    }
  };

  // Preview and production share the handlers below; the computed setState keys need the
  // `as unknown as Pick<State, keyof State>` cast because TS can't narrow a dynamic key literal.
  handleScanDeployedFiles = async (target: "preview" | "production") => {
    const loadingKey = target === "preview" ? "previewScanLoading" : "productionScanLoading";
    if ((this.state as any)[loadingKey]) return;
    this.setState({ [loadingKey]: true } as unknown as Pick<State, keyof State>);
    try {
      const response = await client.admin.apiAdminDeployedFilesScanTargetPost(target);
      if (target === "preview") this.setState({ previewScanResult: response.data ?? null });
      else this.setState({ productionScanResult: response.data ?? null });
    } catch (err) {
      console.error(err);
    } finally {
      this.setState({ [loadingKey]: false } as unknown as Pick<State, keyof State>);
    }
  };

  handleSeedPreviewQueue = async () => {
    if (this.state.seedPreviewLoading) return;
    this.setState({ seedPreviewLoading: true, seedPreviewMessage: null });
    try {
      const response = await client.admin.apiAdminDeploymentQueueSeedPreviewPost();
      const data = response.data ?? null;
      const message = data
        ? `Queued ${data.totalQueued ?? 0} item(s) (${data.pagesQueued ?? 0} pages, ${data.contentQueued ?? 0} content). Skipped ${data.skippedUpToDate ?? 0} up-to-date, ${data.skippedUnsupported ?? 0} unsupported.`
        : "Preview deployment queue seeded.";
      this.setState({ seedPreviewResult: data, seedPreviewMessage: message });
    } catch (err: any) {
      this.setState({ seedPreviewMessage: extractApiError(err, "Seed failed.") });
    } finally {
      this.setState({ seedPreviewLoading: false });
    }
  };

  handleForcePreviewQueue = async () => {
    if (this.state.forcePreviewLoading) return;
    this.setState({ forcePreviewLoading: true, forcePreviewMessage: null });
    try {
      const response = await client.admin.apiAdminDeploymentQueueForcePreviewPost();
      const data = response.data ?? null;
      const message = data
        ? `Queued ${data.totalQueued ?? 0} item(s) (${data.pagesQueued ?? 0} pages, ${data.contentQueued ?? 0} content). Skipped ${data.skippedUnsupported ?? 0} unsupported.`
        : "Preview republish queued.";
      this.setState({ forcePreviewResult: data, forcePreviewMessage: message });
    } catch (err: any) {
      this.setState({ forcePreviewMessage: extractApiError(err, "Force republish failed.") });
    } finally {
      this.setState({ forcePreviewLoading: false });
    }
  };

  handleScanBrokenLinks = async () => {
    if (this.state.linkScanLoading) return;
    this.setState({ linkScanLoading: true, linkScanMessage: null, linkScanResult: null });
    try {
      const response = await client.admin.apiAdminLinksScanPost();
      const data = response.data ?? null;
      const message = data ? `Found ${data.brokenCount ?? 0} broken link(s) out of ${data.totalLinks ?? 0}.` : "Link scan completed.";
      this.setState({ linkScanResult: data, linkScanMessage: message });
    } catch (err: any) {
      this.setState({ linkScanMessage: extractApiError(err, "Link scan failed.") });
    } finally {
      this.setState({ linkScanLoading: false });
    }
  };

  handleRepairDeployedFiles = async (target: "preview" | "production") => {
    const loadingKey = target === "preview" ? "previewFixLoading" : "productionFixLoading";
    if ((this.state as any)[loadingKey]) return;
    this.setState({ [loadingKey]: true } as unknown as Pick<State, keyof State>);
    try {
      const response = await client.admin.apiAdminDeployedFilesRepairTargetPost(target);
      if (target === "preview") this.setState({ previewScanResult: response.data ?? null });
      else this.setState({ productionScanResult: response.data ?? null });
    } catch (err) {
      console.error(err);
    } finally {
      this.setState({ [loadingKey]: false } as unknown as Pick<State, keyof State>);
    }
  };

  handleRecompressAllImages = async () => {
    if (this.state.recompressAllLoading) return;
    this.setState({ recompressAllLoading: true, recompressAllMessage: null });
    try {
      const response = await client.storage.apiStorageRecompressAllPost();
      const data = response.data ?? null;
      const message = data
        ? `Compressed ${data.compressed ?? 0} image(s). Skipped ${data.skipped ?? 0}. Failed ${data.failed ?? 0}.`
        : "Recompress completed.";
      this.setState({ recompressAllResult: data, recompressAllMessage: message });
    } catch (err: any) {
      this.setState({ recompressAllMessage: extractApiError(err, "Recompress failed.") });
    } finally {
      this.setState({ recompressAllLoading: false });
    }
  };

  handleRebuildSearchIndex = async () => {
    if (this.state.rebuildSearchLoading) return;
    this.setState({ rebuildSearchLoading: true, rebuildSearchMessage: "Démarrage du rebuild…" });
    try {
      await axios.post(`${BASE_PATH}/api/search/index/rebuild?target=all&language=fr`);
      await this.pollRebuildSearchStatus();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        this.setState({ rebuildSearchMessage: "Un rebuild est déjà en cours…" });
        await this.pollRebuildSearchStatus();
        return;
      }
      this.setState({ rebuildSearchMessage: extractApiError(err, "Rebuild failed."), rebuildSearchLoading: false });
    }
  };

  // Only the WATCHING is time-boxed, not the job: past maxMs we stop polling and tell the user the
  // rebuild continues server-side, so a tab left open never spins forever.
  pollRebuildSearchStatus = async () => {
    const started = Date.now();
    const maxMs = 20 * 60 * 1000;
    const fmt = (results: Record<string, number>) =>
      Object.keys(results).map((k) => `${k}: ${results[k] >= 0 ? results[k] : "non configuré"}`).join(", ");
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let status: any;
      try {
        const res = await axios.get(`${BASE_PATH}/api/search/index/rebuild/status`);
        status = res?.data?.status;
      } catch { status = undefined; }
      const state = status?.state;
      const results = status?.results ?? {};
      const progress = Object.keys(results).length ? ` (${fmt(results)})` : "";
      if (state === "Completed") {
        this.setState({ rebuildSearchMessage: `Index reconstruit : ${fmt(results)} entrées.`, rebuildSearchLoading: false });
        return;
      }
      if (state === "Failed") {
        this.setState({ rebuildSearchMessage: `Échec du rebuild: ${status?.error ?? "erreur inconnue"}.`, rebuildSearchLoading: false });
        return;
      }
      if (Date.now() - started > maxMs) {
        this.setState({ rebuildSearchMessage: `Rebuild toujours en cours${progress}. Il continue côté serveur : réessayez le statut plus tard.`, rebuildSearchLoading: false });
        return;
      }
      this.setState({ rebuildSearchMessage: `Reconstruction en cours${progress}…` });
      await new Promise((r) => setTimeout(r, 4000));
    }
  };

  // ---- Elasticsearch (moteur de pertinence de la recherche publique) ---------------------------

  loadElasticHealth = async () => {
    try {
      const res = await axios.get(`${BASE_PATH}/api/search/index/es/health`);
      this.setState({ esHealth: res?.data ?? null, esHealthMessage: null });
    } catch {
      // 403 (permission) ou API indisponible : la carte affiche simplement « état indisponible ».
      this.setState({ esHealth: null, esHealthMessage: "État Elasticsearch indisponible." });
    }
  };

  handleRebuildElastic = async () => {
    if (this.state.esRebuildLoading || this.state.rebuildSearchLoading) return;
    this.setState({ esRebuildLoading: true, esRebuildMessage: "Démarrage de la réindexation Elasticsearch…" });
    try {
      await axios.post(`${BASE_PATH}/api/search/index/rebuild?target=all&language=fr&mode=es-only`);
      await this.pollEsRebuildStatus();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        this.setState({ esRebuildMessage: "Un rebuild est déjà en cours…" });
        await this.pollEsRebuildStatus();
        return;
      }
      this.setState({ esRebuildMessage: extractApiError(err, "Échec de la réindexation."), esRebuildLoading: false });
    }
  };

  // Même job partagé que le rebuild complet (le coordinateur n'exécute qu'un job à la fois) : // seul le libellé et l'état local diffèrent.
  pollEsRebuildStatus = async () => {
    const started = Date.now();
    const maxMs = 20 * 60 * 1000;
    const fmt = (results: Record<string, number>) =>
      Object.keys(results)
        .map((k) => `${k}: ${results[k] >= 0 ? results[k] : results[k] === -2 ? "ES désactivé" : "non configuré"}`)
        .join(", ");
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let status: any;
      try {
        const res = await axios.get(`${BASE_PATH}/api/search/index/rebuild/status`);
        status = res?.data?.status;
      } catch { status = undefined; }
      const state = status?.state;
      const results = status?.results ?? {};
      const progress = Object.keys(results).length ? ` (${fmt(results)})` : "";
      if (state === "Completed") {
        this.setState({ esRebuildMessage: `Elasticsearch réindexé : ${fmt(results)} entrées.`, esRebuildLoading: false });
        this.loadElasticHealth();
        return;
      }
      if (state === "Failed") {
        this.setState({ esRebuildMessage: `Échec de la réindexation : ${status?.error ?? "erreur inconnue"}.`, esRebuildLoading: false });
        return;
      }
      if (Date.now() - started > maxMs) {
        this.setState({ esRebuildMessage: `Réindexation toujours en cours${progress}. Elle continue côté serveur.`, esRebuildLoading: false });
        return;
      }
      this.setState({ esRebuildMessage: `Réindexation Elasticsearch en cours${progress}…` });
      await new Promise((r) => setTimeout(r, 4000));
    }
  };

  handleSmartReconcile = async () => {
    if (this.state.smartReconcileLoading) return;
    this.setState({ smartReconcileLoading: true, smartReconcileMessage: null });
    try {
      const res = await axios.post(`${BASE_PATH}/api/admin/maintenance/smart-reconcile`);
      const data = res?.data ?? {};
      const dupes = (data.duplicateSlugs ?? []) as string[];
      const message =
        `Cleared ${data.failedRowsRemoved ?? 0} failed + ${data.orphanRowsRemoved ?? 0} orphan queue row(s), ` +
        `${data.autoPublishRowsRemoved ?? 0} auto-publish phantom(s), ${data.orphanTypeRowsRemoved ?? 0} orphan-type row(s), ` +
        `${data.phantomMarkersCleared ?? 0} phantom marker(s).` +
        (dupes.length ? ` ${dupes.length} duplicate slug(s) flagged: ${dupes.slice(0, 5).join(", ")}${dupes.length > 5 ? "…" : ""}` : " No duplicate pages.");
      this.setState({ smartReconcileMessage: message });
    } catch (err: any) {
      this.setState({ smartReconcileMessage: extractApiError(err, "Smart reconcile failed.") });
    } finally {
      this.setState({ smartReconcileLoading: false });
    }
  };

  // Le bouton « supprimer toutes les redirections » a été RETIRÉ de l'admin sur
  // arbitrage : les règles sont un actif (liens externes, forums), leur perte
  // massive est bien plus grave qu'une file de tâches encombrée. La route API
  // DELETE /api/redirects/all subsiste comme outil d'urgence sans surface UI.

  handleHardClean = async () => {
    if (this.state.hardCleanLoading) return;
    const target = this.state.hardCleanTarget;
    // GARDE-FOU : la purge n'est autorisée QUE sur la preview. En production on republie toujours
    // SANS purge : une purge production impose des heures de reconstruction et vide le registre
    // (ledger) des déploiements.
    const purge = target === "preview";
    const confirmed = window.confirm(
      purge
        ? `Hard clean + republication de la preview ?\n\nLa table de contenu publiée de la preview sera VIDÉE (purge), puis chaque page sera republiée depuis la source (supprime les contenus orphelins). L'opération dure plusieurs minutes.`
        : `Republier toute la production ?\n\nChaque page sera republiée depuis la source, SANS purge : la purge est interdite en production (des heures de reconstruction et le registre des déploiements serait vidé). L'opération dure plusieurs minutes.`
    );
    if (!confirmed) return;
    this.setState({ hardCleanLoading: true, hardCleanMessage: "Démarrage…" });
    try {
      await axios.post(`${BASE_PATH}/api/admin/maintenance/hard-clean/${target}?purge=${purge}`);
      await this.pollHardCleanStatus();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        this.setState({ hardCleanMessage: "Un nettoyage est déjà en cours…" });
        await this.pollHardCleanStatus();
        return;
      }
      this.setState({ hardCleanMessage: extractApiError(err, "Hard clean failed."), hardCleanLoading: false });
    }
  };

  pollHardCleanStatus = async () => {
    const started = Date.now();
    const maxMs = 30 * 60 * 1000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let status: any;
      try {
        const res = await axios.get(`${BASE_PATH}/api/admin/maintenance/hard-clean/status`);
        status = res?.data?.status;
      } catch { status = undefined; }
      const state = status?.state;
      const progress = status?.total ? ` (${status.done ?? 0}/${status.total})` : "";
      if (state === "Completed") {
        this.setState({ hardCleanMessage: `Republié : ${status.deployed ?? 0} page(s) déployée(s), ${status.skipped ?? 0} ignorée(s), ${status.failed ?? 0} en échec.`, hardCleanLoading: false });
        return;
      }
      if (state === "Failed") {
        this.setState({ hardCleanMessage: `Échec: ${status?.error ?? "erreur inconnue"}.`, hardCleanLoading: false });
        return;
      }
      if (Date.now() - started > maxMs) {
        this.setState({ hardCleanMessage: `Toujours en cours${progress}. Continue côté serveur : vérifiez le statut plus tard.`, hardCleanLoading: false });
        return;
      }
      this.setState({ hardCleanMessage: `Republication en cours${progress}…` });
      await new Promise((r) => setTimeout(r, 4000));
    }
  };

  handleRetranslateAll = async () => {
    if (this.state.retranslateLoading) return;
    const confirmed = window.confirm(
      "Recalculer toutes les traductions ?\n\nTraduit, pour chaque langue activée, tous les contenus ayant des champs manquants ou obsolètes (les champs à jour et manuels sont ignorés). Consomme des crédits OpenAI et dure plusieurs minutes. Republiez ensuite pour publier les traductions."
    );
    if (!confirmed) return;
    this.setState({ retranslateLoading: true, retranslateMessage: "Démarrage…" });
    try {
      await axios.post(`${BASE_PATH}/api/admin/maintenance/retranslate-all`);
      await this.pollRetranslateStatus();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        this.setState({ retranslateMessage: "Une tâche de maintenance est déjà en cours…" });
        await this.pollRetranslateStatus();
        return;
      }
      this.setState({ retranslateMessage: extractApiError(err, "Échec du recalcul des traductions."), retranslateLoading: false });
    }
  };

  // Deliberately polls the hard-clean status route: the maintenance coordinator runs one job at a
  // time and publishes ALL maintenance progress through /api/admin/maintenance/hard-clean/status.
  // The status fields are hard-clean-shaped, so for this job the backend maps
  // deployed = documents translated and skipped = fields translated (see
  // ContentMaintenanceCoordinator.RunRetranslateAllAsync) : which is why the completion message
  // below reads those fields that way.
  pollRetranslateStatus = async () => {
    const started = Date.now();
    const maxMs = 30 * 60 * 1000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let status: any;
      try {
        const res = await axios.get(`${BASE_PATH}/api/admin/maintenance/hard-clean/status`);
        status = res?.data?.status;
      } catch { status = undefined; }
      const state = status?.state;
      const progress = status?.total ? ` (${status.done ?? 0}/${status.total})` : "";
      if (state === "Completed") {
        this.setState({ retranslateMessage: `Terminé : ${status.deployed ?? 0} document(s) traduit(s), ${status.skipped ?? 0} champ(s) traduit(s)${(status.failed ?? 0) ? `, ${status.failed} en échec` : ""}. Republiez pour publier les traductions.`, retranslateLoading: false });
        return;
      }
      if (state === "Failed") {
        this.setState({ retranslateMessage: `Échec: ${status?.error ?? "erreur inconnue"}.`, retranslateLoading: false });
        return;
      }
      if (Date.now() - started > maxMs) {
        this.setState({ retranslateMessage: `Toujours en cours${progress}. Continue côté serveur : vérifiez le statut plus tard.`, retranslateLoading: false });
        return;
      }
      this.setState({ retranslateMessage: `Traduction en cours${progress}…` });
      await new Promise((r) => setTimeout(r, 4000));
    }
  };

  renderDeployedSummary = (result: BCCADMINBaseModelsDeployedFileScanResult | BCCADMINBaseModelsDeployedFileRepairResult | null) => {
    if (!result) return null;
    const missing = result.missingCount ?? result.missingInStorage?.length ?? 0;
    const extra = result.extraCount ?? result.extraInStorage?.length ?? 0;
    const mismatch = result.hashMismatchCount ?? result.hashMismatch?.length ?? 0;
    const fixed = (result as BCCADMINBaseModelsDeployedFileRepairResult).fixedCount ?? 0;
    return (
      <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        <p>Missing: {missing} · Extra: {extra} · Hash mismatch: {mismatch}{fixed > 0 ? ` · Fixed: ${fixed}` : ""}</p>
      </div>
    );
  };

  buildAdminLink = (item: BCCADMINBaseModelsBrokenLinkItem): string => {
    const contentType = (item.sourceContentType ?? "").toLowerCase();
    const id = item.sourceContentId ?? "";
    if (!id || !contentType) return "";
    if (contentType === "page") return `/page/${id}`;
    if (contentType === "layout") return "/layout";
    if (contentType === "languages") return "/languages";
    if (contentType === "contact-info") return "/contact-info";
    return `/content/${contentType}/${id}`;
  };

  card = (title: string, description: React.ReactNode, body: React.ReactNode) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
      {body}
    </div>
  );

  // Full static class strings per tone : Tailwind purges classes built by interpolation, so the
  // variants must appear verbatim in the source.
  static BTN_TONES: Record<string, string> = {
    slate: "border-slate-200 text-slate-700 hover:bg-slate-50",
    emerald: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
    sky: "border-sky-200 text-sky-700 hover:bg-sky-50",
    amber: "border-amber-200 text-amber-700 hover:bg-amber-50"
  };

  btn = (label: string, onClick: () => void, loading: boolean, tone: keyof typeof Settings.BTN_TONES = "slate") => (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${Settings.BTN_TONES[tone] ?? Settings.BTN_TONES.slate}`}
    >
      {label}
    </button>
  );

  // Même rendu que `btn`, mais gardé par un jeton de permission : sans lui le bouton reste visible,
  // grisé-cliquable, et le clic explique la permission manquante (le serveur renvoie 403 de toute façon).
  /** État Elasticsearch : joignabilité + docs indexés vs corpus attendu, par cible. */
  renderElasticHealth = () => {
    const data = this.state.esHealth;
    const health = data?.health;
    if (!health) {
      return <p className="text-sm text-slate-500">{this.state.esHealthMessage ?? "État en cours de chargement…"}</p>;
    }
    if (!health.enabled) {
      return <p className="text-sm text-slate-500">Elasticsearch n'est pas configuré sur cet environnement (recherche publique servie par D1).</p>;
    }
    if (!health.reachable) {
      return <p className="text-sm font-medium text-rose-700">Elasticsearch est configuré mais INJOIGNABLE : le site public retombe sur D1.</p>;
    }
    const clusterTone =
      health.clusterStatus === "green" ? "text-emerald-700" :
      health.clusterStatus === "yellow" ? "text-amber-700" : "text-rose-700";
    const targets = health.targets ?? {};
    const expected = data?.expected ?? {};
    const rows = ["preview", "production"].filter((t) => targets[t]);
    return (
      <div className="space-y-1 text-sm text-slate-700">
        <p>
          Cluster : <span className={`font-semibold ${clusterTone}`}>{health.clusterStatus ?? "inconnu"}</span>
        </p>
        {rows.map((t) => {
          const info = targets[t];
          const exp = expected[t];
          const ok = typeof exp === "number" && exp >= 0 && info.entryCount === exp;
          return (
            <p key={t}>
              <span className="font-medium capitalize">{t}</span> :{" "}
              {info.entryCount >= 0 ? `${info.entryCount} document(s)` : "index absent"}
              {typeof exp === "number" && exp >= 0 && (
                <span className={ok ? "text-emerald-700" : "text-amber-700"}> : attendu {exp}{ok ? " ✓" : ""}</span>
              )}
              {info.pdfDocCount >= 0 && <span className="text-slate-500"> · {info.pdfDocCount} page(s)/extrait(s) PDF</span>}
            </p>
          );
        })}
      </div>
    );
  };

  gatedBtn = (label: string, onClick: () => void, loading: boolean, token: string, tone: keyof typeof Settings.BTN_TONES = "slate") => (
    <GatedButton
      token={token}
      onClick={onClick}
      disabled={loading}
      className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${Settings.BTN_TONES[tone] ?? Settings.BTN_TONES.slate}`}
    >
      {label}
    </GatedButton>
  );

  renderIntegrityTab = () => {
    const report = this.state.integrityReport;
    return (
      <div className="space-y-4">
        {/* Analyse approfondie : une carte par contrôle du catalogue code-first /api/checks/catalog
            (run + fix + tendance historisée). Voir Components/Checks/DeepAnalyzePanel. */}
        <DeepAnalyzePanel />
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-base font-semibold text-slate-800">Analyse d'intégrité du site</p>
              <p className="text-sm text-slate-600 max-w-2xl">
                Détecte les ruptures structurelles qui cassent silencieusement le site public : du contenu
                relié par un identifiant migré au lieu de sa clé naturelle, ou des enregistrements stockés
                sous une clé que plus aucun type ne résout (la cause des sections vides / des données figées).
                La correction re-clé les données, re-cuit les widgets et redéploie les pages concernées.
              </p>
            </div>
            {report && (
              <span className={`shrink-0 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${report.isStable ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
                {report.isStable ? "✓ Site stable" : `${report.errorCount} erreur(s)`}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={this.handleIntegrityScan}
              disabled={this.state.integrityScanLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {this.state.integrityScanLoading ? "Analyse en cours…" : "Scanner le site"}
            </button>
            {report && !report.isStable && (
              <GatedButton
                token="feature:settings:write"
                onClick={() => this.handleIntegrityFix()}
                disabled={this.state.integrityFixLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {this.state.integrityFixLoading ? "Correction en cours…" : `Tout corriger (${report.issues.filter((i) => i.fixable).length})`}
              </GatedButton>
            )}
          </div>
          {this.state.integrityMessage && <p className="text-sm text-slate-600">{this.state.integrityMessage}</p>}
        </div>

        {report && (
          <div className="grid gap-3 sm:grid-cols-2">
            {report.checks.map((c) => (
              <div key={c.checkId} className={`rounded-xl border p-4 ${c.issueCount ? "border-rose-200 bg-rose-50/40" : "border-emerald-200 bg-emerald-50/40"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-800">{c.title}</p>
                  <span className={`text-xs font-bold ${c.issueCount ? "text-rose-700" : "text-emerald-700"}`}>{c.issueCount ? `${c.issueCount} problème(s)` : "OK"}</span>
                </div>
                <p className="mt-1 text-xs text-slate-600">{c.description}</p>
              </div>
            ))}
          </div>
        )}

        {report && report.issues.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-800">Problèmes détectés</p>
            <div className="space-y-2">
              {report.issues.map((issue) => (
                <div key={issue.targetId} className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block h-2 w-2 rounded-full ${issue.severity === 1 ? "bg-rose-500" : "bg-amber-500"}`} />
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{issue.checkTitle}</span>
                    </div>
                    <p className="text-sm text-slate-700">{issue.detail}</p>
                    {issue.fixable && <p className="text-xs text-emerald-700">Correction : {issue.fixDescription}</p>}
                  </div>
                  {issue.fixable && (
                    <GatedButton
                      token="feature:settings:write"
                      onClick={() => this.handleIntegrityFix([issue.targetId])}
                      disabled={this.state.integrityFixLoading}
                      className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Corriger
                    </GatedButton>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Réparation « corps HTML → composants » : job de maintenance dédié, voir BodyMigrationCard. */}
        <BodyMigrationCard />
      </div>
    );
  };

  render() {
    const t = this.state.activeTab;
    return (
      // Centered, generously padded column so the page never sits flush against the viewport edge
      // (px-6 / lg:px-10 = the left/right breathing room) and stays a comfortable reading width on
      // wide screens (max-w-6xl + mx-auto). py-8 gives top/bottom air.
      <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8 lg:px-10">
        <Title title="Settings" number={0} />

        {/* Tab bar : sticky so the active section is always reachable while a long tab scrolls. */}
        <div className="sticky top-0 z-10 -mx-1 flex flex-wrap gap-1 border-b border-slate-200 bg-white/95 px-1 backdrop-blur">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => this.setState({ activeTab: tab.id })}
              className={`-mb-px rounded-t-lg border-b-2 px-4 py-2.5 text-sm font-semibold transition ${t === tab.id ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {t === "integrity" && this.renderIntegrityTab()}

        {t === "content" && (
          <div className="grid gap-4 lg:grid-cols-2">
            {this.card("Dynamic paths", "Scan and rebuild dynamic paths for content items.", (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  {this.btn(this.state.scanDynamicLoading ? "Scanning…" : "Scan dynamic paths", this.handleScanDynamicPaths, this.state.scanDynamicLoading)}
                  {this.gatedBtn(this.state.refreshLoading ? "Refreshing…" : "Refresh dynamic paths", this.handleRefreshDynamicPaths, this.state.refreshLoading, "feature:settings:write")}
                </div>
                {this.state.scanDynamicMessage && <p className="text-sm text-slate-600">{this.state.scanDynamicMessage}</p>}
                {this.state.refreshMessage && <p className="text-sm text-slate-600">{this.state.refreshMessage}</p>}
              </>
            ))}
            {this.card("Content maintenance", "Clear stale/failed deployment-queue rows and flag duplicate pages.", (
              <>
                {this.gatedBtn(this.state.smartReconcileLoading ? "Reconciling…" : "Smart reconcile", this.handleSmartReconcile, this.state.smartReconcileLoading, "feature:deployment:write", "emerald")}
                {this.state.smartReconcileMessage && <p className="text-sm text-slate-600">{this.state.smartReconcileMessage}</p>}
              </>
            ))}
            {this.card("Link checker", "Scan internal links (including dynamic page links) and list broken references.", (
              <>
                {this.btn(this.state.linkScanLoading ? "Scanning…" : "Check broken links", this.handleScanBrokenLinks, this.state.linkScanLoading)}
                {this.state.linkScanMessage && <p className="text-sm text-slate-600">{this.state.linkScanMessage}</p>}
                {this.state.linkScanResult?.items?.length ? (
                  <div className="rounded-lg border border-rose-200 bg-rose-50/50 px-3 py-2 text-xs text-rose-700 space-y-2 max-h-64 overflow-auto">
                    {this.state.linkScanResult.items.map((item, index) => {
                      const adminPath = this.buildAdminLink(item);
                      const sourceLabel = item.sourceTitle || item.sourceSlug || item.sourceContentId || "Unknown source";
                      const linkValue = item.linkSlug ? `/${item.linkSlug}` : item.linkPageId ? `page:${item.linkPageId}` : "unknown";
                      return (
                        <div key={`${item.sourceContentId}-${item.linkPath}-${index}`} className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{sourceLabel}</span>
                            <span className="text-rose-600/80">({item.sourceContentType}/{item.sourceLanguage})</span>
                            {adminPath && <Link to={adminPath} className="text-rose-700 underline">Open</Link>}
                          </div>
                          <div><span className="font-medium">Link:</span> {linkValue}{item.linkLabel ? ` · ${item.linkLabel}` : ""}</div>
                          <div><span className="font-medium">Reason:</span> {item.reason || "unknown"}{item.linkPath ? ` · ${item.linkPath}` : ""}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : this.state.linkScanResult ? (
                  <p className="text-sm text-emerald-700">No broken links detected.</p>
                ) : null}
              </>
            ))}
          </div>
        )}

        {t === "deployment" && (
          <div className="grid gap-4 lg:grid-cols-2">
            <SystemTokenCard />
            {this.card("Deployment queue", "Check for missing preview deployments and queue only out-of-date content.", (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  {this.btn(this.state.seedPreviewLoading ? "Checking…" : "Check missing preview deploy", this.handleSeedPreviewQueue, this.state.seedPreviewLoading)}
                  {this.gatedBtn(this.state.forcePreviewLoading ? "Queuing…" : "Force republish preview", this.handleForcePreviewQueue, this.state.forcePreviewLoading, "feature:deployment:write")}
                </div>
                {this.state.seedPreviewMessage && <p className="text-sm text-slate-600">{this.state.seedPreviewMessage}</p>}
                {this.state.forcePreviewMessage && <p className="text-sm text-slate-600">{this.state.forcePreviewMessage}</p>}
              </>
            ))}
            {this.card("Deployed files", "Deep scan Cloudflare storage against deployed records (slow).", (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500">Preview</p>
                  <div className="flex flex-wrap gap-2">
                    {this.btn(this.state.previewScanLoading ? "Scanning…" : "Scan preview", () => this.handleScanDeployedFiles("preview"), this.state.previewScanLoading)}
                    {this.gatedBtn(this.state.previewFixLoading ? "Fixing…" : "Fix preview", () => this.handleRepairDeployedFiles("preview"), this.state.previewFixLoading, "feature:deployment:write")}
                  </div>
                  {this.renderDeployedSummary(this.state.previewScanResult)}
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500">Production</p>
                  <div className="flex flex-wrap gap-2">
                    {this.btn(this.state.productionScanLoading ? "Scanning…" : "Scan production", () => this.handleScanDeployedFiles("production"), this.state.productionScanLoading)}
                    {this.gatedBtn(this.state.productionFixLoading ? "Fixing…" : "Fix production", () => this.handleRepairDeployedFiles("production"), this.state.productionFixLoading, "feature:deployment:write")}
                  </div>
                  {this.renderDeployedSummary(this.state.productionScanResult)}
                </div>
              </div>
            ))}
            <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 space-y-2 lg:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">Danger zone : Hard clean + republish</p>
              <p className="text-sm text-slate-600">
                Republie chaque page depuis la source (supprime les contenus orphelins). Preview : la table
                publiée est d'abord vidée (purge). <span className="font-semibold">Production : republication
                sans purge</span> : une purge production impose des heures de reconstruction et vide le
                registre des déploiements. L'opération dure plusieurs minutes.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={this.state.hardCleanTarget}
                  onChange={(e) => this.setState({ hardCleanTarget: e.target.value as "preview" | "production" })}
                  disabled={this.state.hardCleanLoading}
                  className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm text-rose-700 disabled:opacity-60"
                >
                  <option value="preview">preview</option>
                  <option value="production">production</option>
                </select>
                <GatedButton
                  token="feature:deployment:write"
                  onClick={this.handleHardClean}
                  disabled={this.state.hardCleanLoading}
                  className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {this.state.hardCleanLoading ? "Republishing…" : "Hard clean + republish"}
                </GatedButton>
              </div>
              {this.state.hardCleanMessage && <p className="text-sm text-rose-700">{this.state.hardCleanMessage}</p>}
            </div>
            {/* Deployment power tools moved off the deployment page: queue, link analysis, preview repair,
                preview resync + legacy promote (preview/production selector, production by default). */}
            <AdvancedDeploymentTools />
          </div>
        )}

        {t === "search" && (
          <div className="max-w-2xl space-y-4">
            {this.card(
              "Index de recherche",
              "La publication maintient la recherche du site à jour automatiquement. Ce bouton reconstruit TOUT (fichier JSON, tables D1 et index Elasticsearch, texte des PDF inclus) pour preview + production à partir des pages déployées.",
              (
                <>
                  {this.gatedBtn(this.state.rebuildSearchLoading ? "Reconstruction…" : "Reconstruire l'index de recherche", this.handleRebuildSearchIndex, this.state.rebuildSearchLoading || this.state.esRebuildLoading, "feature:settings:write", "sky")}
                  {this.state.rebuildSearchMessage && <p className="text-sm text-slate-600">{this.state.rebuildSearchMessage}</p>}
                </>
              )
            )}
            {this.card(
              "Elasticsearch (pertinence)",
              "Moteur de la recherche publique avancée : tolérance aux fautes, tri par pertinence, extraits surlignés (PDF inclus, avec numéro de page). S'il est indisponible, le site public retombe automatiquement sur la recherche D1 actuelle.",
              (
                <>
                  {this.renderElasticHealth()}
                  <div className="flex flex-wrap items-center gap-2">
                    {this.gatedBtn(this.state.esRebuildLoading ? "Réindexation…" : "Réindexer Elasticsearch", this.handleRebuildElastic, this.state.esRebuildLoading || this.state.rebuildSearchLoading, "feature:settings:write", "emerald")}
                    <button
                      type="button"
                      onClick={this.loadElasticHealth}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Actualiser l'état
                    </button>
                  </div>
                  {this.state.esRebuildMessage && <p className="text-sm text-slate-600">{this.state.esRebuildMessage}</p>}
                </>
              )
            )}
          </div>
        )}

        {t === "translations" && (
          <div className="max-w-2xl">
            {this.card(
              "Traductions",
              "Recalcule toutes les traductions : pour chaque langue activée, traduit les contenus dont des champs sont manquants ou obsolètes. Consomme des crédits OpenAI ; republiez ensuite pour publier les variantes.",
              (
                <>
                  <GatedButton
                    token="feature:traduction:write"
                    onClick={this.handleRetranslateAll}
                    disabled={this.state.retranslateLoading}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {this.state.retranslateLoading ? "Traduction en cours…" : "Recalculer toutes les traductions"}
                  </GatedButton>
                  {this.state.retranslateMessage && <p className="text-sm text-blue-700">{this.state.retranslateMessage}</p>}
                </>
              )
            )}
          </div>
        )}

        {t === "media" && (
          <div className="space-y-4">
            <div className="max-w-2xl">
              {this.card("Image compression", "Recompress all images in storage (max width 1200px, quality 80). Smaller images are not upscaled.", (
                <>
                  {this.gatedBtn(this.state.recompressAllLoading ? "Recompressing…" : "Recompress all images", this.handleRecompressAllImages, this.state.recompressAllLoading, "feature:settings:write", "amber")}
                  {this.state.recompressAllMessage && <p className="text-sm text-slate-600">{this.state.recompressAllMessage}</p>}
                  {this.state.recompressAllResult && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <p>Total: {this.state.recompressAllResult.total ?? 0} · Compressed: {this.state.recompressAllResult.compressed ?? 0} · Skipped: {this.state.recompressAllResult.skipped ?? 0} · Failed: {this.state.recompressAllResult.failed ?? 0}</p>
                    </div>
                  )}
                </>
              ))}
            </div>
            {/* Audit lecture seule de la médiathèque (orphelins + fuites PdfExtractedTexts). */}
            <MediaOrphansCard />
          </div>
        )}
      </div>
    );
  }
}

const connectedComponent = withStore(
  Settings,
  (state) => ({ ...state.settings }),
  { ...settingsStore.actionCreators }
);

export default withRouter(connectedComponent);
